/**
 * Astrological Interpretations
 * 
 * Provides meanings and interpretations for astrological chart elements
 * Note: These are traditional astrological interpretations, not scientifically validated
 */

import type { Planet, ZodiacSign, Aspect } from '../../types/astrology.types';

export interface PlanetInterpretation {
  meaning: string;
  strengths: string[];
  challenges: string[];
  keywords: string[];
}

export interface SignInterpretation {
  description: string;
  traits: string[];
  shadow: string[];
}

export interface AspectInterpretation {
  meaning: string;
  expression: string;
  challenges: string;
}

/**
 * Get interpretation for a planet in a sign
 */
export function getPlanetInSignInterpretation(planet: Planet, sign: ZodiacSign): PlanetInterpretation {
  const interpretations: Record<string, Record<string, PlanetInterpretation>> = {
    Sun: {
      Aries: {
        meaning: 'Bold, pioneering spirit with strong leadership qualities',
        strengths: ['Courage', 'Initiative', 'Independence', 'Enthusiasm'],
        challenges: ['Impatience', 'Impulsiveness', 'Self-centeredness'],
        keywords: ['Leader', 'Pioneer', 'Energetic', 'Assertive']
      },
      Taurus: {
        meaning: 'Steady, reliable nature with appreciation for beauty and stability',
        strengths: ['Patience', 'Reliability', 'Sensuality', 'Practicality'],
        challenges: ['Stubbornness', 'Possessiveness', 'Resistance to change'],
        keywords: ['Stable', 'Sensual', 'Practical', 'Enduring']
      },
      Gemini: {
        meaning: 'Curious, communicative nature with quick wit and adaptability',
        strengths: ['Communication', 'Versatility', 'Intelligence', 'Social'],
        challenges: ['Restlessness', 'Superficiality', 'Indecisiveness'],
        keywords: ['Communicative', 'Adaptable', 'Curious', 'Versatile']
      },
      Cancer: {
        meaning: 'Nurturing, emotional nature with strong intuition and protective instincts',
        strengths: ['Nurturing', 'Intuitive', 'Protective', 'Emotional depth'],
        challenges: ['Moodiness', 'Over-sensitivity', 'Clinginess'],
        keywords: ['Nurturing', 'Intuitive', 'Emotional', 'Protective']
      },
      Leo: {
        meaning: 'Confident, creative nature with natural leadership and generosity',
        strengths: ['Confidence', 'Creativity', 'Generosity', 'Leadership'],
        challenges: ['Pride', 'Dramatic', 'Need for attention'],
        keywords: ['Confident', 'Creative', 'Generous', 'Dramatic']
      },
      Virgo: {
        meaning: 'Analytical, service-oriented nature with attention to detail',
        strengths: ['Analytical', 'Practical', 'Service-oriented', 'Detail-oriented'],
        challenges: ['Perfectionism', 'Criticism', 'Worry'],
        keywords: ['Analytical', 'Practical', 'Meticulous', 'Helpful']
      },
      Libra: {
        meaning: 'Harmonious, diplomatic nature with love of beauty and balance',
        strengths: ['Diplomatic', 'Harmonious', 'Aesthetic', 'Fair'],
        challenges: ['Indecisiveness', 'People-pleasing', 'Avoidance of conflict'],
        keywords: ['Harmonious', 'Diplomatic', 'Balanced', 'Aesthetic']
      },
      Scorpio: {
        meaning: 'Intense, transformative nature with deep emotional and psychological insight',
        strengths: ['Intensity', 'Transformation', 'Depth', 'Passion'],
        challenges: ['Jealousy', 'Secretiveness', 'Control issues'],
        keywords: ['Intense', 'Transformative', 'Deep', 'Mysterious']
      },
      Sagittarius: {
        meaning: 'Adventurous, philosophical nature with love of freedom and truth',
        strengths: ['Adventurous', 'Philosophical', 'Optimistic', 'Honest'],
        challenges: ['Restlessness', 'Tactlessness', 'Overconfidence'],
        keywords: ['Adventurous', 'Philosophical', 'Optimistic', 'Free']
      },
      Capricorn: {
        meaning: 'Ambitious, disciplined nature with strong sense of responsibility',
        strengths: ['Ambitious', 'Disciplined', 'Responsible', 'Practical'],
        challenges: ['Rigidity', 'Pessimism', 'Workaholism'],
        keywords: ['Ambitious', 'Disciplined', 'Responsible', 'Structured']
      },
      Aquarius: {
        meaning: 'Innovative, humanitarian nature with independent and progressive thinking',
        strengths: ['Innovative', 'Humanitarian', 'Independent', 'Progressive'],
        challenges: ['Detachment', 'Rebelliousness', 'Eccentricity'],
        keywords: ['Innovative', 'Humanitarian', 'Independent', 'Unconventional']
      },
      Pisces: {
        meaning: 'Intuitive, compassionate nature with artistic and spiritual sensitivity',
        strengths: ['Intuitive', 'Compassionate', 'Artistic', 'Spiritual'],
        challenges: ['Escapism', 'Boundary issues', 'Victim mentality'],
        keywords: ['Intuitive', 'Compassionate', 'Dreamy', 'Spiritual']
      }
    },
    Moon: {
      Aries: {
        meaning: 'Emotional responses are quick and direct, with need for independence',
        strengths: ['Quick reactions', 'Independence', 'Courage', 'Spontaneity'],
        challenges: ['Impatience', 'Anger', 'Selfishness'],
        keywords: ['Impulsive', 'Independent', 'Direct', 'Fiery']
      },
      Taurus: {
        meaning: 'Emotional stability and need for security and comfort',
        strengths: ['Stability', 'Loyalty', 'Sensuality', 'Patience'],
        challenges: ['Possessiveness', 'Stubbornness', 'Resistance to change'],
        keywords: ['Stable', 'Sensual', 'Loyal', 'Comfortable']
      },
      Gemini: {
        meaning: 'Emotional nature is changeable and communicative',
        strengths: ['Adaptability', 'Communication', 'Curiosity', 'Versatility'],
        challenges: ['Restlessness', 'Superficiality', 'Nervousness'],
        keywords: ['Changeable', 'Communicative', 'Curious', 'Nervous']
      },
      Cancer: {
        meaning: 'Deeply emotional and nurturing, with strong connection to home and family',
        strengths: ['Nurturing', 'Intuitive', 'Protective', 'Emotional depth'],
        challenges: ['Moodiness', 'Over-sensitivity', 'Clinginess'],
        keywords: ['Nurturing', 'Emotional', 'Protective', 'Sensitive']
      },
      Leo: {
        meaning: 'Emotional expression is dramatic and warm-hearted',
        strengths: ['Warmth', 'Generosity', 'Dramatic expression', 'Loyalty'],
        challenges: ['Drama', 'Need for attention', 'Pride'],
        keywords: ['Dramatic', 'Warm', 'Generous', 'Proud']
      },
      Virgo: {
        meaning: 'Emotional responses are practical and analytical',
        strengths: ['Practicality', 'Helpfulness', 'Analytical', 'Service'],
        challenges: ['Worry', 'Criticism', 'Perfectionism'],
        keywords: ['Practical', 'Analytical', 'Helpful', 'Worried']
      },
      Libra: {
        meaning: 'Emotional need for harmony and partnership',
        strengths: ['Harmony', 'Diplomacy', 'Aesthetic sense', 'Cooperation'],
        challenges: ['Indecisiveness', 'People-pleasing', 'Avoidance'],
        keywords: ['Harmonious', 'Diplomatic', 'Balanced', 'Indecisive']
      },
      Scorpio: {
        meaning: 'Intense emotional depth with transformative power',
        strengths: ['Intensity', 'Depth', 'Transformation', 'Passion'],
        challenges: ['Jealousy', 'Secretiveness', 'Control'],
        keywords: ['Intense', 'Deep', 'Transformative', 'Secretive']
      },
      Sagittarius: {
        meaning: 'Emotional nature is optimistic and freedom-loving',
        strengths: ['Optimism', 'Adventure', 'Philosophy', 'Honesty'],
        challenges: ['Restlessness', 'Tactlessness', 'Overconfidence'],
        keywords: ['Optimistic', 'Adventurous', 'Philosophical', 'Free']
      },
      Capricorn: {
        meaning: 'Emotional responses are controlled and responsible',
        strengths: ['Control', 'Responsibility', 'Discipline', 'Maturity'],
        challenges: ['Emotional repression', 'Pessimism', 'Coldness'],
        keywords: ['Controlled', 'Responsible', 'Disciplined', 'Reserved']
      },
      Aquarius: {
        meaning: 'Emotional nature is detached and humanitarian',
        strengths: ['Detachment', 'Humanitarianism', 'Independence', 'Innovation'],
        challenges: ['Emotional distance', 'Rebelliousness', 'Eccentricity'],
        keywords: ['Detached', 'Humanitarian', 'Independent', 'Unconventional']
      },
      Pisces: {
        meaning: 'Highly sensitive and intuitive emotional nature',
        strengths: ['Intuition', 'Compassion', 'Artistic', 'Spiritual'],
        challenges: ['Escapism', 'Boundary issues', 'Victim mentality'],
        keywords: ['Intuitive', 'Compassionate', 'Dreamy', 'Escapist']
      }
    },
    Mercury: {
      Aries: {
        meaning: 'Quick, direct communication style with assertive thinking and pioneering ideas',
        strengths: ['Directness', 'Quick thinking', 'Initiative', 'Bold ideas'],
        challenges: ['Impatience in communication', 'Tactlessness', 'Rash decisions'],
        keywords: ['Direct', 'Quick', 'Assertive', 'Pioneering']
      },
      Taurus: {
        meaning: 'Steady, practical thinking with methodical communication and focus on tangible results',
        strengths: ['Practical thinking', 'Methodical', 'Reliable communication', 'Common sense'],
        challenges: ['Stubborn thinking', 'Resistance to new ideas', 'Slow to change mind'],
        keywords: ['Practical', 'Steady', 'Methodical', 'Grounded']
      },
      Gemini: {
        meaning: 'Versatile, quick-witted communication with natural curiosity and adaptability',
        strengths: ['Versatility', 'Quick wit', 'Curiosity', 'Adaptability', 'Communication skills'],
        challenges: ['Scattered thinking', 'Superficiality', 'Restlessness', 'Indecisiveness'],
        keywords: ['Versatile', 'Curious', 'Communicative', 'Adaptable']
      },
      Cancer: {
        meaning: 'Intuitive, emotional communication style with strong memory and protective expression',
        strengths: ['Intuitive communication', 'Emotional intelligence', 'Strong memory', 'Nurturing expression'],
        challenges: ['Moodiness in communication', 'Over-sensitivity', 'Tendency to take things personally'],
        keywords: ['Intuitive', 'Emotional', 'Memory', 'Nurturing']
      },
      Leo: {
        meaning: 'Dramatic, expressive communication with creative flair and natural leadership in ideas',
        strengths: ['Dramatic expression', 'Creative communication', 'Confidence', 'Leadership in ideas'],
        challenges: ['Dramatization', 'Need for attention', 'Pride in opinions'],
        keywords: ['Dramatic', 'Creative', 'Confident', 'Expressive']
      },
      Virgo: {
        meaning: 'Analytical, precise thinking with attention to detail and practical problem-solving',
        strengths: ['Analytical thinking', 'Precision', 'Attention to detail', 'Practical problem-solving'],
        challenges: ['Over-analysis', 'Criticism', 'Worry', 'Perfectionism'],
        keywords: ['Analytical', 'Precise', 'Detail-oriented', 'Practical']
      },
      Libra: {
        meaning: 'Diplomatic, balanced communication with aesthetic sense and desire for harmony',
        strengths: ['Diplomacy', 'Balance', 'Aesthetic sense', 'Fair communication'],
        challenges: ['Indecisiveness', 'People-pleasing', 'Avoidance of conflict'],
        keywords: ['Diplomatic', 'Balanced', 'Aesthetic', 'Harmonious']
      },
      Scorpio: {
        meaning: 'Deep, penetrating thinking with psychological insight and transformative communication',
        strengths: ['Depth', 'Psychological insight', 'Transformation', 'Intensity'],
        challenges: ['Secretiveness', 'Suspicion', 'Manipulation', 'Obsessive thinking'],
        keywords: ['Deep', 'Penetrating', 'Transformative', 'Intense']
      },
      Sagittarius: {
        meaning: 'Philosophical, expansive thinking with love of learning and truth-seeking',
        strengths: ['Philosophical thinking', 'Expansive ideas', 'Love of learning', 'Truth-seeking'],
        challenges: ['Tactlessness', 'Overconfidence', 'Dogmatism', 'Lack of detail'],
        keywords: ['Philosophical', 'Expansive', 'Learner', 'Truthful']
      },
      Capricorn: {
        meaning: 'Structured, disciplined thinking with practical planning and long-term vision',
        strengths: ['Structured thinking', 'Discipline', 'Practical planning', 'Long-term vision'],
        challenges: ['Rigidity', 'Pessimism', 'Overly serious', 'Resistance to new ideas'],
        keywords: ['Structured', 'Disciplined', 'Practical', 'Ambitious']
      },
      Aquarius: {
        meaning: 'Innovative, original thinking with humanitarian ideals and progressive communication',
        strengths: ['Innovation', 'Originality', 'Humanitarian ideals', 'Progressive thinking'],
        challenges: ['Detachment', 'Eccentricity', 'Rebelliousness', 'Unpredictability'],
        keywords: ['Innovative', 'Original', 'Humanitarian', 'Progressive']
      },
      Pisces: {
        meaning: 'Intuitive, imaginative thinking with artistic sensitivity and spiritual understanding',
        strengths: ['Intuition', 'Imagination', 'Artistic sensitivity', 'Spiritual understanding'],
        challenges: ['Confusion', 'Escapism', 'Lack of boundaries', 'Unclear communication'],
        keywords: ['Intuitive', 'Imaginative', 'Artistic', 'Spiritual']
      }
    },
    Venus: {
      Aries: {
        meaning: 'Passionate, direct approach to love and values with independent and assertive expression',
        strengths: ['Passion', 'Directness', 'Independence', 'Assertiveness'],
        challenges: ['Impatience', 'Selfishness', 'Competitiveness', 'Lack of tact'],
        keywords: ['Passionate', 'Direct', 'Independent', 'Assertive']
      },
      Taurus: {
        meaning: 'Sensual, stable approach to love with appreciation for beauty, comfort, and material pleasures',
        strengths: ['Sensuality', 'Stability', 'Appreciation of beauty', 'Loyalty'],
        challenges: ['Possessiveness', 'Stubbornness', 'Materialism', 'Resistance to change'],
        keywords: ['Sensual', 'Stable', 'Beautiful', 'Loyal']
      },
      Gemini: {
        meaning: 'Versatile, communicative approach to love with need for mental stimulation and variety',
        strengths: ['Versatility', 'Communication', 'Mental stimulation', 'Adaptability'],
        challenges: ['Restlessness', 'Superficiality', 'Indecisiveness', 'Lack of depth'],
        keywords: ['Versatile', 'Communicative', 'Curious', 'Adaptable']
      },
      Cancer: {
        meaning: 'Nurturing, emotional approach to love with strong need for security and emotional connection',
        strengths: ['Nurturing', 'Emotional depth', 'Loyalty', 'Protectiveness'],
        challenges: ['Moodiness', 'Over-sensitivity', 'Clinginess', 'Possessiveness'],
        keywords: ['Nurturing', 'Emotional', 'Loyal', 'Protective']
      },
      Leo: {
        meaning: 'Dramatic, generous approach to love with need for admiration and creative expression',
        strengths: ['Generosity', 'Dramatic expression', 'Loyalty', 'Creativity'],
        challenges: ['Need for attention', 'Pride', 'Dramatization', 'Possessiveness'],
        keywords: ['Generous', 'Dramatic', 'Loyal', 'Creative']
      },
      Virgo: {
        meaning: 'Practical, service-oriented approach to love with attention to detail and helpfulness',
        strengths: ['Practicality', 'Service', 'Attention to detail', 'Helpfulness'],
        challenges: ['Criticism', 'Perfectionism', 'Worry', 'Over-analysis'],
        keywords: ['Practical', 'Service-oriented', 'Detail-oriented', 'Helpful']
      },
      Libra: {
        meaning: 'Harmonious, diplomatic approach to love with strong need for partnership and balance',
        strengths: ['Harmony', 'Diplomacy', 'Partnership', 'Aesthetic sense'],
        challenges: ['Indecisiveness', 'People-pleasing', 'Avoidance of conflict', 'Dependency'],
        keywords: ['Harmonious', 'Diplomatic', 'Partnership', 'Aesthetic']
      },
      Scorpio: {
        meaning: 'Intense, transformative approach to love with deep passion and psychological depth',
        strengths: ['Intensity', 'Passion', 'Depth', 'Transformation'],
        challenges: ['Jealousy', 'Possessiveness', 'Secretiveness', 'Control issues'],
        keywords: ['Intense', 'Passionate', 'Deep', 'Transformative']
      },
      Sagittarius: {
        meaning: 'Adventurous, freedom-loving approach to love with philosophical and optimistic expression',
        strengths: ['Adventure', 'Freedom', 'Philosophy', 'Optimism'],
        challenges: ['Restlessness', 'Commitment issues', 'Tactlessness', 'Overconfidence'],
        keywords: ['Adventurous', 'Free', 'Philosophical', 'Optimistic']
      },
      Capricorn: {
        meaning: 'Practical, responsible approach to love with focus on stability and long-term commitment',
        strengths: ['Responsibility', 'Stability', 'Commitment', 'Practicality'],
        challenges: ['Emotional reserve', 'Pessimism', 'Rigidity', 'Material focus'],
        keywords: ['Responsible', 'Stable', 'Committed', 'Practical']
      },
      Aquarius: {
        meaning: 'Independent, unconventional approach to love with humanitarian ideals and friendship focus',
        strengths: ['Independence', 'Unconventional', 'Humanitarian', 'Friendship'],
        challenges: ['Detachment', 'Emotional distance', 'Rebelliousness', 'Unpredictability'],
        keywords: ['Independent', 'Unconventional', 'Humanitarian', 'Friendly']
      },
      Pisces: {
        meaning: 'Compassionate, idealistic approach to love with artistic sensitivity and spiritual connection',
        strengths: ['Compassion', 'Idealism', 'Artistic sensitivity', 'Spiritual connection'],
        challenges: ['Escapism', 'Boundary issues', 'Victim mentality', 'Confusion'],
        keywords: ['Compassionate', 'Idealistic', 'Artistic', 'Spiritual']
      }
    },
    Mars: {
      Aries: {
        meaning: 'Direct, assertive action with pioneering spirit and natural leadership drive',
        strengths: ['Assertiveness', 'Courage', 'Initiative', 'Leadership'],
        challenges: ['Impatience', 'Aggressiveness', 'Impulsiveness', 'Selfishness'],
        keywords: ['Assertive', 'Courageous', 'Pioneering', 'Direct']
      },
      Taurus: {
        meaning: 'Steady, persistent action with focus on stability and material security',
        strengths: ['Persistence', 'Stability', 'Endurance', 'Practical action'],
        challenges: ['Stubbornness', 'Possessiveness', 'Resistance to change', 'Slow to act'],
        keywords: ['Persistent', 'Stable', 'Enduring', 'Practical']
      },
      Gemini: {
        meaning: 'Versatile, quick action with mental agility and communicative drive',
        strengths: ['Versatility', 'Quick action', 'Mental agility', 'Communication'],
        challenges: ['Restlessness', 'Scattered energy', 'Lack of focus', 'Nervousness'],
        keywords: ['Versatile', 'Quick', 'Mental', 'Communicative']
      },
      Cancer: {
        meaning: 'Emotional, protective action with strong need for security and nurturing drive',
        strengths: ['Protectiveness', 'Emotional drive', 'Nurturing action', 'Loyalty'],
        challenges: ['Moodiness', 'Passive-aggressiveness', 'Over-sensitivity', 'Defensiveness'],
        keywords: ['Protective', 'Emotional', 'Nurturing', 'Loyal']
      },
      Leo: {
        meaning: 'Dramatic, confident action with creative expression and leadership drive',
        strengths: ['Confidence', 'Dramatic expression', 'Creativity', 'Leadership'],
        challenges: ['Pride', 'Dramatization', 'Need for attention', 'Arrogance'],
        keywords: ['Confident', 'Dramatic', 'Creative', 'Leader']
      },
      Virgo: {
        meaning: 'Precise, service-oriented action with attention to detail and practical efficiency',
        strengths: ['Precision', 'Service', 'Efficiency', 'Practical action'],
        challenges: ['Criticism', 'Perfectionism', 'Worry', 'Over-analysis'],
        keywords: ['Precise', 'Service-oriented', 'Efficient', 'Practical']
      },
      Libra: {
        meaning: 'Diplomatic, balanced action with focus on harmony and partnership',
        strengths: ['Diplomacy', 'Balance', 'Cooperation', 'Harmony'],
        challenges: ['Indecisiveness', 'Passive-aggressiveness', 'Avoidance of conflict', 'Lack of assertiveness'],
        keywords: ['Diplomatic', 'Balanced', 'Cooperative', 'Harmonious']
      },
      Scorpio: {
        meaning: 'Intense, transformative action with deep passion and psychological power',
        strengths: ['Intensity', 'Transformation', 'Depth', 'Power'],
        challenges: ['Jealousy', 'Secretiveness', 'Control', 'Manipulation'],
        keywords: ['Intense', 'Transformative', 'Deep', 'Powerful']
      },
      Sagittarius: {
        meaning: 'Adventurous, expansive action with philosophical drive and freedom-seeking',
        strengths: ['Adventure', 'Expansion', 'Philosophy', 'Freedom'],
        challenges: ['Restlessness', 'Tactlessness', 'Overconfidence', 'Lack of focus'],
        keywords: ['Adventurous', 'Expansive', 'Philosophical', 'Free']
      },
      Capricorn: {
        meaning: 'Disciplined, ambitious action with focus on achievement and long-term goals',
        strengths: ['Discipline', 'Ambition', 'Achievement', 'Long-term focus'],
        challenges: ['Rigidity', 'Pessimism', 'Workaholism', 'Emotional repression'],
        keywords: ['Disciplined', 'Ambitious', 'Achievement-oriented', 'Structured']
      },
      Aquarius: {
        meaning: 'Innovative, independent action with humanitarian ideals and progressive drive',
        strengths: ['Innovation', 'Independence', 'Humanitarian', 'Progressive'],
        challenges: ['Rebelliousness', 'Detachment', 'Eccentricity', 'Unpredictability'],
        keywords: ['Innovative', 'Independent', 'Humanitarian', 'Progressive']
      },
      Pisces: {
        meaning: 'Intuitive, compassionate action with artistic sensitivity and spiritual drive',
        strengths: ['Intuition', 'Compassion', 'Artistic', 'Spiritual'],
        challenges: ['Passivity', 'Escapism', 'Lack of boundaries', 'Confusion'],
        keywords: ['Intuitive', 'Compassionate', 'Artistic', 'Spiritual']
      }
    },
    Jupiter: {
      Aries: {
        meaning: 'Expansive, pioneering optimism with bold faith and adventurous spirit',
        strengths: ['Optimism', 'Pioneering spirit', 'Bold faith', 'Adventure'],
        challenges: ['Overconfidence', 'Impatience', 'Impulsiveness', 'Lack of planning'],
        keywords: ['Optimistic', 'Pioneering', 'Bold', 'Adventurous']
      },
      Taurus: {
        meaning: 'Steady, material expansion with focus on security and tangible abundance',
        strengths: ['Material abundance', 'Stability', 'Practical expansion', 'Security'],
        challenges: ['Materialism', 'Stubbornness', 'Resistance to change', 'Over-indulgence'],
        keywords: ['Abundant', 'Stable', 'Practical', 'Secure']
      },
      Gemini: {
        meaning: 'Expansive, curious learning with versatile knowledge and communicative growth',
        strengths: ['Curiosity', 'Versatile learning', 'Communication', 'Mental expansion'],
        challenges: ['Scattered learning', 'Superficiality', 'Restlessness', 'Lack of depth'],
        keywords: ['Curious', 'Versatile', 'Communicative', 'Mental']
      },
      Cancer: {
        meaning: 'Nurturing, emotional expansion with focus on home, family, and emotional security',
        strengths: ['Nurturing', 'Emotional expansion', 'Home focus', 'Protectiveness'],
        challenges: ['Over-protectiveness', 'Moodiness', 'Clinginess', 'Emotional dependency'],
        keywords: ['Nurturing', 'Emotional', 'Home-oriented', 'Protective']
      },
      Leo: {
        meaning: 'Dramatic, generous expansion with creative expression and leadership growth',
        strengths: ['Generosity', 'Creative expansion', 'Leadership', 'Confidence'],
        challenges: ['Pride', 'Overconfidence', 'Dramatization', 'Need for attention'],
        keywords: ['Generous', 'Creative', 'Leader', 'Confident']
      },
      Virgo: {
        meaning: 'Practical, service-oriented expansion with focus on improvement and helpfulness',
        strengths: ['Practical expansion', 'Service', 'Improvement', 'Helpfulness'],
        challenges: ['Perfectionism', 'Criticism', 'Worry', 'Over-analysis'],
        keywords: ['Practical', 'Service-oriented', 'Improving', 'Helpful']
      },
      Libra: {
        meaning: 'Harmonious, balanced expansion with focus on partnership and aesthetic growth',
        strengths: ['Harmony', 'Balance', 'Partnership', 'Aesthetic growth'],
        challenges: ['Indecisiveness', 'People-pleasing', 'Avoidance of conflict', 'Dependency'],
        keywords: ['Harmonious', 'Balanced', 'Partnership', 'Aesthetic']
      },
      Scorpio: {
        meaning: 'Intense, transformative expansion with deep psychological and spiritual growth',
        strengths: ['Intensity', 'Transformation', 'Depth', 'Spiritual growth'],
        challenges: ['Obsessiveness', 'Secretiveness', 'Control', 'Manipulation'],
        keywords: ['Intense', 'Transformative', 'Deep', 'Spiritual']
      },
      Sagittarius: {
        meaning: 'Philosophical, expansive growth with love of learning and truth-seeking',
        strengths: ['Philosophy', 'Expansion', 'Learning', 'Truth-seeking'],
        challenges: ['Overconfidence', 'Tactlessness', 'Dogmatism', 'Restlessness'],
        keywords: ['Philosophical', 'Expansive', 'Learner', 'Truthful']
      },
      Capricorn: {
        meaning: 'Structured, ambitious expansion with focus on achievement and long-term growth',
        strengths: ['Structure', 'Ambition', 'Achievement', 'Long-term growth'],
        challenges: ['Rigidity', 'Pessimism', 'Workaholism', 'Emotional repression'],
        keywords: ['Structured', 'Ambitious', 'Achievement-oriented', 'Disciplined']
      },
      Aquarius: {
        meaning: 'Innovative, humanitarian expansion with progressive ideals and independent growth',
        strengths: ['Innovation', 'Humanitarian', 'Progressive', 'Independent growth'],
        challenges: ['Detachment', 'Rebelliousness', 'Eccentricity', 'Unpredictability'],
        keywords: ['Innovative', 'Humanitarian', 'Progressive', 'Independent']
      },
      Pisces: {
        meaning: 'Compassionate, spiritual expansion with artistic sensitivity and intuitive growth',
        strengths: ['Compassion', 'Spiritual expansion', 'Artistic', 'Intuition'],
        challenges: ['Escapism', 'Boundary issues', 'Confusion', 'Victim mentality'],
        keywords: ['Compassionate', 'Spiritual', 'Artistic', 'Intuitive']
      }
    },
    Saturn: {
      Aries: {
        meaning: 'Disciplined, structured action with focus on responsibility and self-control',
        strengths: ['Discipline', 'Structure', 'Responsibility', 'Self-control'],
        challenges: ['Rigidity', 'Frustration', 'Impatience', 'Self-doubt'],
        keywords: ['Disciplined', 'Structured', 'Responsible', 'Controlled']
      },
      Taurus: {
        meaning: 'Steady, practical discipline with focus on material security and stability',
        strengths: ['Practical discipline', 'Stability', 'Material security', 'Endurance'],
        challenges: ['Stubbornness', 'Materialism', 'Resistance to change', 'Pessimism'],
        keywords: ['Practical', 'Stable', 'Secure', 'Enduring']
      },
      Gemini: {
        meaning: 'Structured, disciplined thinking with focus on practical communication and learning',
        strengths: ['Structured thinking', 'Disciplined communication', 'Practical learning', 'Organization'],
        challenges: ['Over-analysis', 'Worry', 'Criticism', 'Mental rigidity'],
        keywords: ['Structured', 'Disciplined', 'Practical', 'Organized']
      },
      Cancer: {
        meaning: 'Emotional discipline with focus on family responsibility and emotional security',
        strengths: ['Emotional discipline', 'Family responsibility', 'Security', 'Protectiveness'],
        challenges: ['Emotional repression', 'Moodiness', 'Over-protectiveness', 'Pessimism'],
        keywords: ['Emotionally disciplined', 'Family-oriented', 'Secure', 'Protective']
      },
      Leo: {
        meaning: 'Structured, responsible creativity with focus on achievement and leadership',
        strengths: ['Structured creativity', 'Responsible leadership', 'Achievement', 'Discipline'],
        challenges: ['Rigidity', 'Pessimism', 'Over-control', 'Lack of spontaneity'],
        keywords: ['Structured', 'Responsible', 'Achievement-oriented', 'Disciplined']
      },
      Virgo: {
        meaning: 'Precise, practical discipline with focus on service and improvement',
        strengths: ['Precision', 'Practical discipline', 'Service', 'Improvement'],
        challenges: ['Perfectionism', 'Criticism', 'Worry', 'Over-analysis'],
        keywords: ['Precise', 'Practical', 'Service-oriented', 'Improving']
      },
      Libra: {
        meaning: 'Structured, responsible partnerships with focus on balance and commitment',
        strengths: ['Structured partnerships', 'Responsibility', 'Balance', 'Commitment'],
        challenges: ['Rigidity', 'Pessimism', 'Indecisiveness', 'Over-control'],
        keywords: ['Structured', 'Responsible', 'Balanced', 'Committed']
      },
      Scorpio: {
        meaning: 'Intense, transformative discipline with focus on deep psychological work',
        strengths: ['Intense discipline', 'Transformation', 'Depth', 'Psychological work'],
        challenges: ['Rigidity', 'Secretiveness', 'Control', 'Pessimism'],
        keywords: ['Intense', 'Transformative', 'Deep', 'Disciplined']
      },
      Sagittarius: {
        meaning: 'Structured, disciplined philosophy with focus on practical wisdom and truth',
        strengths: ['Structured philosophy', 'Disciplined learning', 'Practical wisdom', 'Truth'],
        challenges: ['Rigidity', 'Dogmatism', 'Pessimism', 'Over-control'],
        keywords: ['Structured', 'Disciplined', 'Practical', 'Wise']
      },
      Capricorn: {
        meaning: 'Ambitious, disciplined structure with focus on achievement and long-term goals',
        strengths: ['Ambition', 'Discipline', 'Achievement', 'Long-term focus'],
        challenges: ['Rigidity', 'Pessimism', 'Workaholism', 'Emotional repression'],
        keywords: ['Ambitious', 'Disciplined', 'Achievement-oriented', 'Structured']
      },
      Aquarius: {
        meaning: 'Structured, disciplined innovation with focus on practical progress and responsibility',
        strengths: ['Structured innovation', 'Disciplined progress', 'Practical responsibility', 'Organization'],
        challenges: ['Rigidity', 'Detachment', 'Pessimism', 'Over-control'],
        keywords: ['Structured', 'Disciplined', 'Practical', 'Organized']
      },
      Pisces: {
        meaning: 'Structured, disciplined spirituality with focus on practical compassion and service',
        strengths: ['Structured spirituality', 'Disciplined compassion', 'Practical service', 'Responsibility'],
        challenges: ['Rigidity', 'Escapism', 'Pessimism', 'Over-control'],
        keywords: ['Structured', 'Disciplined', 'Practical', 'Compassionate']
      }
    },
    Uranus: {
      Aries: {
        meaning: 'Revolutionary, pioneering innovation with bold independence and sudden change',
        strengths: ['Revolutionary', 'Pioneering', 'Bold independence', 'Sudden change'],
        challenges: ['Rebelliousness', 'Impatience', 'Impulsiveness', 'Unpredictability'],
        keywords: ['Revolutionary', 'Pioneering', 'Independent', 'Sudden']
      },
      Taurus: {
        meaning: 'Innovative, unconventional approach to material security and stability',
        strengths: ['Innovative security', 'Unconventional stability', 'Material innovation', 'Practical change'],
        challenges: ['Resistance to change', 'Stubbornness', 'Unpredictability', 'Rebelliousness'],
        keywords: ['Innovative', 'Unconventional', 'Material', 'Practical']
      },
      Gemini: {
        meaning: 'Revolutionary, innovative communication with sudden insights and progressive ideas',
        strengths: ['Revolutionary communication', 'Innovative ideas', 'Sudden insights', 'Progressive thinking'],
        challenges: ['Unpredictability', 'Restlessness', 'Rebelliousness', 'Eccentricity'],
        keywords: ['Revolutionary', 'Innovative', 'Sudden', 'Progressive']
      },
      Cancer: {
        meaning: 'Innovative, unconventional approach to home and family with sudden emotional changes',
        strengths: ['Innovative home', 'Unconventional family', 'Sudden emotional change', 'Progressive security'],
        challenges: ['Unpredictability', 'Emotional instability', 'Rebelliousness', 'Eccentricity'],
        keywords: ['Innovative', 'Unconventional', 'Sudden', 'Progressive']
      },
      Leo: {
        meaning: 'Revolutionary, innovative creativity with bold self-expression and sudden inspiration',
        strengths: ['Revolutionary creativity', 'Innovative expression', 'Bold inspiration', 'Sudden creativity'],
        challenges: ['Rebelliousness', 'Unpredictability', 'Eccentricity', 'Over-independence'],
        keywords: ['Revolutionary', 'Innovative', 'Bold', 'Sudden']
      },
      Virgo: {
        meaning: 'Innovative, progressive approach to service and improvement with sudden insights',
        strengths: ['Innovative service', 'Progressive improvement', 'Sudden insights', 'Practical innovation'],
        challenges: ['Unpredictability', 'Rebelliousness', 'Eccentricity', 'Over-analysis'],
        keywords: ['Innovative', 'Progressive', 'Sudden', 'Practical']
      },
      Libra: {
        meaning: 'Revolutionary, innovative approach to partnerships and balance with sudden changes',
        strengths: ['Revolutionary partnerships', 'Innovative balance', 'Sudden changes', 'Progressive harmony'],
        challenges: ['Unpredictability', 'Rebelliousness', 'Indecisiveness', 'Eccentricity'],
        keywords: ['Revolutionary', 'Innovative', 'Sudden', 'Progressive']
      },
      Scorpio: {
        meaning: 'Revolutionary, transformative innovation with deep psychological and spiritual change',
        strengths: ['Revolutionary transformation', 'Innovative depth', 'Sudden change', 'Progressive transformation'],
        challenges: ['Unpredictability', 'Intensity', 'Rebelliousness', 'Eccentricity'],
        keywords: ['Revolutionary', 'Transformative', 'Sudden', 'Progressive']
      },
      Sagittarius: {
        meaning: 'Revolutionary, innovative philosophy with progressive ideals and sudden expansion',
        strengths: ['Revolutionary philosophy', 'Innovative expansion', 'Sudden growth', 'Progressive ideals'],
        challenges: ['Unpredictability', 'Rebelliousness', 'Overconfidence', 'Eccentricity'],
        keywords: ['Revolutionary', 'Innovative', 'Sudden', 'Progressive']
      },
      Capricorn: {
        meaning: 'Revolutionary, innovative structure with progressive achievement and sudden change',
        strengths: ['Revolutionary structure', 'Innovative achievement', 'Sudden change', 'Progressive discipline'],
        challenges: ['Unpredictability', 'Rebelliousness', 'Rigidity', 'Eccentricity'],
        keywords: ['Revolutionary', 'Innovative', 'Sudden', 'Progressive']
      },
      Aquarius: {
        meaning: 'Revolutionary, innovative independence with progressive humanitarian ideals',
        strengths: ['Revolutionary independence', 'Innovative humanitarian', 'Sudden change', 'Progressive ideals'],
        challenges: ['Rebelliousness', 'Detachment', 'Eccentricity', 'Unpredictability'],
        keywords: ['Revolutionary', 'Innovative', 'Independent', 'Progressive']
      },
      Pisces: {
        meaning: 'Revolutionary, innovative spirituality with progressive compassion and sudden inspiration',
        strengths: ['Revolutionary spirituality', 'Innovative compassion', 'Sudden inspiration', 'Progressive intuition'],
        challenges: ['Unpredictability', 'Escapism', 'Rebelliousness', 'Eccentricity'],
        keywords: ['Revolutionary', 'Innovative', 'Sudden', 'Progressive']
      }
    },
    Neptune: {
      Aries: {
        meaning: 'Inspired, visionary action with spiritual idealism and intuitive pioneering',
        strengths: ['Inspiration', 'Visionary action', 'Spiritual idealism', 'Intuitive pioneering'],
        challenges: ['Confusion', 'Escapism', 'Lack of boundaries', 'Idealistic delusion'],
        keywords: ['Inspired', 'Visionary', 'Spiritual', 'Intuitive']
      },
      Taurus: {
        meaning: 'Dreamy, idealistic approach to material security with spiritual values',
        strengths: ['Dreamy security', 'Idealistic values', 'Spiritual material', 'Intuitive stability'],
        challenges: ['Confusion', 'Escapism', 'Material delusion', 'Lack of boundaries'],
        keywords: ['Dreamy', 'Idealistic', 'Spiritual', 'Intuitive']
      },
      Gemini: {
        meaning: 'Inspired, intuitive communication with spiritual ideas and imaginative thinking',
        strengths: ['Inspired communication', 'Intuitive ideas', 'Spiritual thinking', 'Imaginative expression'],
        challenges: ['Confusion', 'Escapism', 'Lack of clarity', 'Idealistic delusion'],
        keywords: ['Inspired', 'Intuitive', 'Spiritual', 'Imaginative']
      },
      Cancer: {
        meaning: 'Dreamy, idealistic approach to home and family with spiritual nurturing',
        strengths: ['Dreamy home', 'Idealistic family', 'Spiritual nurturing', 'Intuitive protection'],
        challenges: ['Confusion', 'Escapism', 'Emotional delusion', 'Lack of boundaries'],
        keywords: ['Dreamy', 'Idealistic', 'Spiritual', 'Intuitive']
      },
      Leo: {
        meaning: 'Inspired, visionary creativity with spiritual expression and idealistic leadership',
        strengths: ['Inspired creativity', 'Visionary expression', 'Spiritual leadership', 'Idealistic confidence'],
        challenges: ['Confusion', 'Escapism', 'Delusion', 'Lack of boundaries'],
        keywords: ['Inspired', 'Visionary', 'Spiritual', 'Idealistic']
      },
      Virgo: {
        meaning: 'Dreamy, idealistic approach to service with spiritual improvement and intuitive healing',
        strengths: ['Dreamy service', 'Idealistic improvement', 'Spiritual healing', 'Intuitive practicality'],
        challenges: ['Confusion', 'Escapism', 'Perfectionist delusion', 'Lack of boundaries'],
        keywords: ['Dreamy', 'Idealistic', 'Spiritual', 'Intuitive']
      },
      Libra: {
        meaning: 'Inspired, idealistic partnerships with spiritual harmony and visionary balance',
        strengths: ['Inspired partnerships', 'Idealistic harmony', 'Spiritual balance', 'Visionary cooperation'],
        challenges: ['Confusion', 'Escapism', 'Idealistic delusion', 'Lack of boundaries'],
        keywords: ['Inspired', 'Idealistic', 'Spiritual', 'Visionary']
      },
      Scorpio: {
        meaning: 'Dreamy, transformative spirituality with deep psychological and mystical insight',
        strengths: ['Dreamy transformation', 'Idealistic depth', 'Spiritual insight', 'Mystical power'],
        challenges: ['Confusion', 'Escapism', 'Delusion', 'Lack of boundaries'],
        keywords: ['Dreamy', 'Transformative', 'Spiritual', 'Mystical']
      },
      Sagittarius: {
        meaning: 'Inspired, visionary philosophy with spiritual expansion and idealistic truth',
        strengths: ['Inspired philosophy', 'Visionary expansion', 'Spiritual truth', 'Idealistic learning'],
        challenges: ['Confusion', 'Escapism', 'Idealistic delusion', 'Lack of boundaries'],
        keywords: ['Inspired', 'Visionary', 'Spiritual', 'Idealistic']
      },
      Capricorn: {
        meaning: 'Dreamy, idealistic structure with spiritual achievement and visionary discipline',
        strengths: ['Dreamy structure', 'Idealistic achievement', 'Spiritual discipline', 'Visionary ambition'],
        challenges: ['Confusion', 'Escapism', 'Delusion', 'Lack of boundaries'],
        keywords: ['Dreamy', 'Idealistic', 'Spiritual', 'Visionary']
      },
      Aquarius: {
        meaning: 'Inspired, visionary innovation with spiritual humanitarian ideals and progressive compassion',
        strengths: ['Inspired innovation', 'Visionary humanitarian', 'Spiritual ideals', 'Progressive compassion'],
        challenges: ['Confusion', 'Escapism', 'Idealistic delusion', 'Lack of boundaries'],
        keywords: ['Inspired', 'Visionary', 'Spiritual', 'Progressive']
      },
      Pisces: {
        meaning: 'Dreamy, idealistic spirituality with deep compassion and intuitive understanding',
        strengths: ['Dreamy spirituality', 'Idealistic compassion', 'Spiritual understanding', 'Intuitive depth'],
        challenges: ['Confusion', 'Escapism', 'Delusion', 'Lack of boundaries'],
        keywords: ['Dreamy', 'Idealistic', 'Spiritual', 'Intuitive']
      }
    },
    Pluto: {
      Aries: {
        meaning: 'Intense, transformative action with deep psychological power and revolutionary change',
        strengths: ['Intense transformation', 'Deep power', 'Revolutionary change', 'Psychological strength'],
        challenges: ['Obsessiveness', 'Control', 'Manipulation', 'Destructive tendencies'],
        keywords: ['Intense', 'Transformative', 'Powerful', 'Revolutionary']
      },
      Taurus: {
        meaning: 'Deep, transformative approach to material security with intense focus on values',
        strengths: ['Deep transformation', 'Intense security', 'Transformative values', 'Powerful stability'],
        challenges: ['Obsessiveness', 'Possessiveness', 'Control', 'Destructive tendencies'],
        keywords: ['Deep', 'Transformative', 'Intense', 'Powerful']
      },
      Gemini: {
        meaning: 'Intense, transformative communication with deep psychological insight and powerful ideas',
        strengths: ['Intense communication', 'Transformative ideas', 'Deep insight', 'Powerful expression'],
        challenges: ['Obsessiveness', 'Manipulation', 'Control', 'Destructive tendencies'],
        keywords: ['Intense', 'Transformative', 'Deep', 'Powerful']
      },
      Cancer: {
        meaning: 'Deep, transformative approach to home and family with intense emotional power',
        strengths: ['Deep transformation', 'Intense emotional power', 'Transformative home', 'Powerful protection'],
        challenges: ['Obsessiveness', 'Control', 'Manipulation', 'Destructive tendencies'],
        keywords: ['Deep', 'Transformative', 'Intense', 'Powerful']
      },
      Leo: {
        meaning: 'Intense, transformative creativity with deep psychological power and revolutionary expression',
        strengths: ['Intense creativity', 'Transformative expression', 'Deep power', 'Revolutionary confidence'],
        challenges: ['Obsessiveness', 'Control', 'Manipulation', 'Destructive tendencies'],
        keywords: ['Intense', 'Transformative', 'Deep', 'Powerful']
      },
      Virgo: {
        meaning: 'Deep, transformative approach to service with intense focus on improvement and healing',
        strengths: ['Deep transformation', 'Intense service', 'Transformative improvement', 'Powerful healing'],
        challenges: ['Obsessiveness', 'Control', 'Perfectionism', 'Destructive tendencies'],
        keywords: ['Deep', 'Transformative', 'Intense', 'Powerful']
      },
      Libra: {
        meaning: 'Intense, transformative partnerships with deep psychological balance and powerful harmony',
        strengths: ['Intense transformation', 'Transformative partnerships', 'Deep balance', 'Powerful harmony'],
        challenges: ['Obsessiveness', 'Control', 'Manipulation', 'Destructive tendencies'],
        keywords: ['Intense', 'Transformative', 'Deep', 'Powerful']
      },
      Scorpio: {
        meaning: 'Deep, intense transformation with powerful psychological insight and revolutionary change',
        strengths: ['Deep transformation', 'Intense power', 'Transformative insight', 'Revolutionary change'],
        challenges: ['Obsessiveness', 'Control', 'Manipulation', 'Destructive tendencies'],
        keywords: ['Deep', 'Intense', 'Transformative', 'Powerful']
      },
      Sagittarius: {
        meaning: 'Intense, transformative philosophy with deep psychological expansion and powerful truth',
        strengths: ['Intense transformation', 'Transformative philosophy', 'Deep expansion', 'Powerful truth'],
        challenges: ['Obsessiveness', 'Control', 'Dogmatism', 'Destructive tendencies'],
        keywords: ['Intense', 'Transformative', 'Deep', 'Powerful']
      },
      Capricorn: {
        meaning: 'Deep, transformative structure with intense focus on achievement and powerful discipline',
        strengths: ['Deep transformation', 'Intense achievement', 'Transformative structure', 'Powerful discipline'],
        challenges: ['Obsessiveness', 'Control', 'Rigidity', 'Destructive tendencies'],
        keywords: ['Deep', 'Transformative', 'Intense', 'Powerful']
      },
      Aquarius: {
        meaning: 'Intense, transformative innovation with deep psychological progress and revolutionary ideals',
        strengths: ['Intense transformation', 'Transformative innovation', 'Deep progress', 'Revolutionary ideals'],
        challenges: ['Obsessiveness', 'Control', 'Rebelliousness', 'Destructive tendencies'],
        keywords: ['Intense', 'Transformative', 'Deep', 'Powerful']
      },
      Pisces: {
        meaning: 'Deep, transformative spirituality with intense compassion and powerful intuitive understanding',
        strengths: ['Deep transformation', 'Intense spirituality', 'Transformative compassion', 'Powerful intuition'],
        challenges: ['Obsessiveness', 'Escapism', 'Confusion', 'Destructive tendencies'],
        keywords: ['Deep', 'Transformative', 'Intense', 'Powerful']
      }
    }
  };

  // Default interpretation if specific combination not found
  const defaultInterpretation: PlanetInterpretation = {
    meaning: `${planet} in ${sign} brings unique qualities to your chart`,
    strengths: ['Unique expression', 'Individuality'],
    challenges: ['Integration', 'Balance'],
    keywords: [planet, sign]
  };

  return interpretations[planet]?.[sign] || defaultInterpretation;
}

/**
 * Get interpretation for an aspect
 */
export function getAspectInterpretation(aspect: Aspect): AspectInterpretation {
  const interpretations: Record<string, AspectInterpretation> = {
    conjunction: {
      meaning: 'Planets work together, blending their energies',
      expression: 'Intense focus and concentration of energy',
      challenges: 'Can be overwhelming or one-sided if not balanced'
    },
    opposition: {
      meaning: 'Planets create tension and need for balance',
      expression: 'Awareness of polarities and need for integration',
      challenges: 'Can create conflict or projection onto others'
    },
    trine: {
      meaning: 'Harmonious flow of energy between planets',
      expression: 'Natural talents and ease of expression',
      challenges: 'May lead to complacency or taking gifts for granted'
    },
    square: {
      meaning: 'Challenging dynamic that creates tension and growth',
      expression: 'Motivation to overcome obstacles and develop strength',
      challenges: 'Can create frustration, conflict, or internal struggle'
    },
    sextile: {
      meaning: 'Opportunity for growth and positive expression',
      expression: 'Natural talents and opportunities for development',
      challenges: 'May require conscious effort to fully utilize'
    }
  };

  return interpretations[aspect.type] || {
    meaning: 'Aspect creates dynamic interaction between planets',
    expression: 'Unique expression of planetary energies',
    challenges: 'Integration and balance required'
  };
}

/**
 * Get house interpretation
 */
export function getHouseInterpretation(house: number): { meaning: string; areas: string[] } {
  const houses: Record<number, { meaning: string; areas: string[] }> = {
    1: {
      meaning: 'Self, identity, appearance, first impressions',
      areas: ['Personality', 'Physical appearance', 'How others see you', 'Self-image']
    },
    2: {
      meaning: 'Resources, values, possessions, self-worth',
      areas: ['Money', 'Material possessions', 'Values', 'Self-worth', 'Talents']
    },
    3: {
      meaning: 'Communication, siblings, learning, short journeys',
      areas: ['Communication', 'Siblings', 'Education', 'Writing', 'Local travel']
    },
    4: {
      meaning: 'Home, family, roots, private life',
      areas: ['Home', 'Family', 'Childhood', 'Private life', 'Foundation']
    },
    5: {
      meaning: 'Creativity, romance, children, self-expression',
      areas: ['Creativity', 'Romance', 'Children', 'Hobbies', 'Pleasure']
    },
    6: {
      meaning: 'Work, health, daily routines, service',
      areas: ['Work', 'Health', 'Daily routines', 'Service', 'Pets']
    },
    7: {
      meaning: 'Partnerships, relationships, others',
      areas: ['Marriage', 'Partnerships', 'Close relationships', 'Others', 'Contracts']
    },
    8: {
      meaning: 'Transformation, shared resources, mysteries',
      areas: ['Transformation', 'Shared resources', 'Death and rebirth', 'Mysteries', 'Intimacy']
    },
    9: {
      meaning: 'Philosophy, higher learning, travel, expansion',
      areas: ['Philosophy', 'Higher education', 'Travel', 'Religion', 'Expansion']
    },
    10: {
      meaning: 'Career, public image, reputation, authority',
      areas: ['Career', 'Public image', 'Reputation', 'Authority', 'Achievement']
    },
    11: {
      meaning: 'Friends, groups, hopes, dreams',
      areas: ['Friends', 'Groups', 'Hopes', 'Dreams', 'Social causes']
    },
    12: {
      meaning: 'Subconscious, spirituality, hidden matters, karma',
      areas: ['Subconscious', 'Spirituality', 'Hidden matters', 'Karma', 'Solitude']
    }
  };

  return houses[house] || {
    meaning: 'House of life experience',
    areas: ['Life experience', 'Growth', 'Development']
  };
}

